import React, { useState, useMemo } from "react";
import Button, { ButtonProps } from "@mui/material/Button";
import { useApolloClient } from "@apollo/client";
import {
  ExportConfirmationDialog,
  ExportErrorDialog,
  ExportLimitDialog,
  ExportProgressDialog,
  ExportLoadingDialog,
} from "./ExportDialogs";
import { ExportService } from "./ExportService";
import { GridColumns, GridValidRowModel, GridValueFormatterParams, GridValueGetterParams } from "@mui/x-data-grid-pro";
import { createSvgIcon } from "@mui/material/utils";
import formatDate from "date-fns/format";

const ExportIcon = createSvgIcon(
  <path d="M19 12v7H5v-7H3v7c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2v-7h-2zm-6 .67l2.59-2.58L17 11.5l-5 5-5-5 1.41-1.41L11 12.67V3h2z" />,
  "SaveAlt",
);

/**
 * A custom export button component that handles exporting all data matching the filter criteria.
 */
export const CustomExportButton = <TRow extends GridValidRowModel>({
  getTotalCount,
  fetchDataBatch,
  transformDataToCSV,
  fileName = `export_${Date.now()}`,
  batchSize = 100,
  warningThreshold = 5000,
  maxRowsLimit = 10000,
  buttonLabel = "Export",
  columns = [],
  sx,
  "aria-label": ariaLabel,
}: CustomExportButtonProps<TRow>): JSX.Element => {
  const client = useApolloClient();
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const [showLimitDialog, setShowLimitDialog] = useState(false);
  const [showProgressDialog, setShowProgressDialog] = useState(false);
  const [showLoadingDialog, setShowLoadingDialog] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  const [rowCount, setRowCount] = useState(0);
  const [error, setError] = useState<unknown>(null);
  const [exportService, setExportService] = useState<ExportService<TRow> | null>(null);

  // Filter out action columns
  const exportColumns = useMemo(() => columns.filter((col) => col.type !== "actions"), [columns]);

  // Format date in a way Excel can recognize as a date (YYYY-MM-DD)
  const formatDateForExcel = React.useCallback((date: Date): string => {
    return formatDate(date, "yyyy-MM-dd");
  }, []);

  // Format datetime in a way Excel can recognize (YYYY-MM-DD HH:MM:SS)
  const formatDateTimeForExcel = React.useCallback((date: Date): string => {
    return formatDate(date, "yyyy-MM-dd HH:mm:ss");
  }, []);

  // Helper to format and escape CSV values
  const formatCsvValue = React.useCallback(
    (value: unknown): string => {
      if (value === null || value === undefined) {
        return "";
      } else if (typeof value === "string") {
        return `"${value.replace(/"/g, '""')}"`;
      } else if (value instanceof Date) {
        // Handle Date objects that weren't caught by column type formatting
        return `"${formatDateForExcel(value)}"`;
      } else {
        return String(value);
      }
    },
    [formatDateForExcel],
  );

  // Custom CSV transformation based on column definitions
  const transformToFormattedCSV = useMemo(() => {
    return (data: TRow[]): string => {
      if (!data || data.length === 0) {
        return "";
      }

      // Use column headers from GridColDef
      const headers = exportColumns.map((col) => col.headerName || col.field);

      // Create CSV rows with formatted values
      const rows = data.map((item) => {
        return exportColumns
          .map((column) => {
            const field = column.field;
            const rawValue = (item as Record<string, unknown>)[field];

            // Apply value getter if defined
            let processedValue = rawValue;
            if (column.valueGetter) {
              try {
                processedValue = column.valueGetter({
                  row: item,
                } as unknown as GridValueGetterParams<unknown, TRow>);
              } catch (e) {
                console.error(`Error in valueGetter for field ${field}:`, e);
              }
            }

            // Apply value formatter if defined (after value getter)
            if (column.valueFormatter) {
              try {
                processedValue = column.valueFormatter({
                  value: processedValue,
                  field,
                  // Some formatters look up the full row via `api.getRow(id)` (the grid provides
                  // this at render time). `id` just needs to be non-null; `getRow` ignores it and
                  // always returns this row, since we're formatting one row at a time here.
                  id: 0,
                  api: { getRow: () => item },
                } as unknown as GridValueFormatterParams<unknown>);
              } catch (e) {
                console.error(`Error in valueFormatter for field ${field}:`, e);
              }
            }
            // If no formatter but column is a date type, apply date formatting
            else if (column.type === "date" && processedValue instanceof Date) {
              processedValue = formatDateForExcel(processedValue);
            }
            // If no formatter but column is a dateTime type, apply dateTime formatting
            else if (column.type === "dateTime" && processedValue instanceof Date) {
              processedValue = formatDateTimeForExcel(processedValue);
            }

            // Format the value for CSV
            return formatCsvValue(processedValue);
          })
          .join(",");
      });

      // Combine headers and rows
      return [headers.join(","), ...rows].join("\n");
    };
  }, [exportColumns, formatCsvValue, formatDateForExcel, formatDateTimeForExcel]);

  const handleExportClick = async () => {
    // Show loading dialog immediately
    setShowLoadingDialog(true);
    try {
      const service = new ExportService(client, {
        getTotalCount,
        fetchDataBatch,
        transformDataToCSV: transformDataToCSV || transformToFormattedCSV,
        batchSize,
        warningThreshold,
        maxRowsLimit,
        onProgress: (current, total) => {
          setProgress({ current, total });
        },
        fileName,
      });

      setExportService(service);

      const { shouldWarn, exceedsLimit, total } = await service.checkExportSize();
      setRowCount(total);
      setProgress({ current: 0, total });

      // Hide loading dialog since we now know the export size
      setShowLoadingDialog(false);

      if (exceedsLimit) {
        setShowLimitDialog(true);
      } else if (shouldWarn) {
        setShowWarningDialog(true);
      } else {
        startExport(service);
      }
    } catch (err) {
      setError(err);
      setShowLoadingDialog(false);
      setShowErrorDialog(true);
    }
  };

  const startExport = async (service: ExportService<TRow>) => {
    setShowProgressDialog(true);

    try {
      const csvData = await service.exportData();
      ExportService.downloadCSV(csvData, fileName);

      // Keep the progress dialog open for a moment so the user sees it completed
      setTimeout(() => {
        setShowProgressDialog(false);
      }, 1000);
    } catch (err) {
      setError(err);
      setShowErrorDialog(true);
    }
  };

  const handleConfirmExport = () => {
    setShowWarningDialog(false);
    if (exportService) {
      startExport(exportService);
    }
  };

  const handleCancelExport = () => {
    setShowWarningDialog(false);
    setExportService(null);
  };

  const handleCloseErrorDialog = () => {
    setShowErrorDialog(false);
    setShowProgressDialog(false);
    setError(null);
  };

  const handleCancelProgress = () => {
    if (exportService) {
      exportService.cancelExport();
    }
    setShowProgressDialog(false);
  };

  const handleCancelLoading = () => {
    setShowLoadingDialog(false);
    if (exportService) {
      exportService.cancelExport();
      setExportService(null);
    }
  };

  return (
    <>
      <Button
        startIcon={<ExportIcon />}
        onClick={handleExportClick}
        size="small"
        aria-label={ariaLabel}
        sx={{
          textTransform: "none",
          ...sx,
        }}
      >
        {buttonLabel}
      </Button>

      <ExportLoadingDialog open={showLoadingDialog} onCancel={handleCancelLoading} />

      <ExportConfirmationDialog
        open={showWarningDialog}
        onCancel={handleCancelExport}
        onConfirm={handleConfirmExport}
        rowCount={rowCount}
      />

      <ExportProgressDialog
        open={showProgressDialog}
        onCancel={handleCancelProgress}
        current={progress.current}
        total={progress.total}
      />

      <ExportErrorDialog open={showErrorDialog} onClose={handleCloseErrorDialog} error={error} />

      <ExportLimitDialog
        open={showLimitDialog}
        onClose={() => setShowLimitDialog(false)}
        rowCount={rowCount}
        maxLimit={maxRowsLimit}
      />
    </>
  );
};

export interface CustomExportButtonProps<TRow extends GridValidRowModel> {
  /**
   * Function to fetch the total count of records matching the current filter
   */
  getTotalCount: () => Promise<number>;

  /**
   * Function to fetch batches of data for export
   * @param offset - Starting position
   * @param limit - Number of records to retrieve
   * @returns Promise resolving to array of records
   */
  fetchDataBatch: (offset: number, limit: number) => Promise<TRow[]>;

  /**
   * Function to transform data into CSV format
   * If not provided, a default transformation will be used
   */
  transformDataToCSV?: (data: unknown[]) => string;

  /**
   * Name of the file to be downloaded (without extension)
   * @default "export_{timestamp}"
   */
  fileName?: string;

  /**
   * Maximum number of rows to fetch per batch
   * @default 100
   */
  batchSize?: number;

  /**
   * Number of rows that will trigger a warning dialog
   * @default 5000
   */
  warningThreshold?: number;

  /**
   * Maximum number of rows allowed for export
   * @default 10000
   */
  maxRowsLimit?: number;

  /**
   * Custom button label
   * @default "Export"
   */
  buttonLabel?: string;

  /**
   * Column definitions from DataGrid
   * Used to format the CSV export to match the table display
   */
  columns?: GridColumns<TRow>;

  /**
   * MUI SX props for the button
   */
  sx?: ButtonProps["sx"];

  /**
   * Accessible label for screen readers — required when buttonLabel is empty
   */
  "aria-label"?: string;
}

export default CustomExportButton;
